import { createStyles, Theme } from '@material-ui/core/styles';
import { ThemeFlags } from 'types/theme';

const styleBorderBottom = (color: string, themeFlags: ThemeFlags) => ({
  borderBottom: themeFlags.boxStyle === 'stroke' ? `2px solid ${color} !important` : 'none',
});

const styleTextCase = (value: string) => ({
  '& input, textarea': {
    textTransform: value,

    '&::placeholder': {
      textTransform: 'none',
    },
  },
});

const styles = ({
  spacing, colors, typography, flags,
}: Theme) => createStyles({
  inputRoot: {
    ...typography.textRegular,
    padding: spacing(2),
    outline: flags.boxStyle === 'stroke' ? 'none' : `1px solid ${colors.inputs.inputField.enabled.stroke}`,
    borderRadius: colors.inputs.inputField.corner,
    '&:hover': {
      outlineColor: colors.inputs.inputField.hovered.stroke,
    },
  },
  small: {
    ...typography.textSmallCompact,
    padding: spacing(1),
    paddingLeft: spacing(2),
    '& $input': {
      '&::placeholder': {
        ...typography.textSmallCompact,
      },
    },
  },
  root: {
    width: '100%',
    ...styleTextCase('none'),
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[autocomplete=on]:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 30px ${colors.layout.backgroundPrimary} inset !important`,
      WebkitTextFillColor: colors.inputs.inputField.textRegular,
    },
  },
  input: {
    color: colors.inputs.inputField.textRegular,
    padding: 0,
    height: 'auto',
    '&::placeholder': {
      ...typography.textRegular,
      color: colors.inputs.inputField.textPlaceholder,
    },
  },
  label: {
    ...typography.textSmallCompact,
    color: `${colors.inputs.label.name} !important`,
    height: spacing(2),
    padding: 0,
    top: `-${spacing(1)}px`,
  },
  labelState: {
    '&$label': {
      color: colors.inputs.label.name,
    },
  },
  underline: {
    '&:after': {
      display: 'none',
    },
    '&&:before': {
      ...styleBorderBottom(colors.inputs.inputField.enabled.stroke, flags),
    },
    '&&:hover:before': {
      ...styleBorderBottom(colors.inputs.inputField.hovered.stroke, flags),
    },
    '&.Mui-disabled:before': {
      borderBottom: `2px solid ${colors.inputs.inputField.disabled.stroke}!important`,
    },
  },
  focused: {
    '&:before': {
      borderImage: flags.boxStyle === 'stroke' ? colors.inputs.inputField.focused.stroke : 'none',
      borderImageSlice: 1,
    },
    outline: `2px solid ${colors.inputs.inputField.focused.stroke}`,
    '& path': {
      fill: colors.inputs.inputField.focused.prefixIcon,
    },
  },
  error: {
    color: colors.inputs.inputField.textError,
    outlineColor: colors.inputs.inputField.error.stroke,
    '&&:before': {
      ...styleBorderBottom(colors.inputs.inputField.error.stroke, flags),
      borderImage: 'none',
    },
    '&&:hover:before': {
      ...styleBorderBottom(colors.inputs.inputField.error.stroke, flags),
    },
    '& path': {
      fill: colors.inputs.inputField.error.prefixIcon,
    },
    '&:hover': {
      outlineColor: colors.inputs.inputField.error.stroke,
    },
  },
  disabled: {
    background: colors.inputs.inputField.disabled.fill,
    color: colors.inputs.inputField.textDisabled,
    outlineColor: colors.inputs.inputField.disabled.stroke,
    '&&:before': {
      ...styleBorderBottom(colors.inputs.inputField.disabled.stroke, flags),
    },
    '&&:hover:before': {
      ...styleBorderBottom(colors.inputs.inputField.disabled.stroke, flags),
    },
    '& path': {
      fill: colors.inputs.inputField.disabled.prefixIcon,
    },
    '&:hover': {
      outlineColor: colors.inputs.inputField.disabled.stroke,
    },
  },
  textUppercase: {
    ...styleTextCase('uppercase'),
  },
  textLowercase: {
    ...styleTextCase('lowercase'),
  },
  displayNone: {
    '&&': { display: 'none' },
  },
  autoExpandNone: {
    '&&': {
      resize: 'none',
      overflowY: 'hidden',
      padding: 0,
    },
  },
  autoExpandVertical: {
    resize: 'vertical',
  },
  adornments: {
    '& > .MuiTypography-root': {
      ...typography.textRegular,
      color: colors.inputs.inputField.enabled.prefixIcon,
    },
    '& path': {
      fill: colors.inputs.inputField.enabled.prefixIcon,
    },
  },
  endAdornments: {
    '& > .MuiTypography-root': {
      ...typography.textRegular,
      color: colors.inputs.inputField.enabled.prefixIcon,
    },
    '& path': {
      fill: colors.controls.buttonIcon.enabled.fill,
    },
  },
  asterisk: {
    color: colors.inputs.label.requiredMark,
  },
});

export default styles;
